import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import type { QueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { CocoEvent } from '@cotera/client/app/etc';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type {
  FolderItemsResponse,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceSelectionModel } from '../selection/resource-selection.model';
import {
  favoriteRefOfItem,
  type FavoriteItem,
} from '../favorites/use-favorites';

// All optional: the palette runs it with an empty payload (target from the open
// resource, direction toggled from current state); `useToggleFavorite` passes
// the resource and the resolved direction so the star and this agree exactly.
const inputSchema = z.object({
  type: z.nativeEnum(ResourceType).optional(),
  id: z.string().optional(),
  favorite: z.boolean().optional(),
});

type Params = z.infer<typeof inputSchema>;

type FavoriteTarget = { type: ResourceType; id: string };

/**
 * The one thing that writes a favorite to the API.
 *
 * Every favorite toggle ends here: the star, row menu and sidebar go through
 * `useToggleFavorite` (which drives the optimistic overlay, then dispatches
 * this), and the command palette dispatches it directly. Nothing else calls
 * `client.resource.favorites.set`. When no target is supplied it reads the open
 * resource from the shared {@link ResourceSelectionModel} (every page syncs and
 * targets it — dataset and trigger have no dedicated model, only this one).
 */
export class ToggleFavoriteAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Toggle Favorite';
  description = 'Star or unstar the current resource';
  inputSchema = inputSchema;
  group = 'app';
  icon = 'star';

  private _lastToggle: { target: FavoriteTarget; favorite: boolean } | null =
    null;

  constructor(
    private readonly client: Pick<TenantedClient, 'resource'>,
    private readonly queryClient: QueryClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTarget({}, context) !== null;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const target = this.getTarget(params, context);
    if (target === null) {
      return err([{ t: 'error', message: 'No resource to favorite' }]);
    }

    const favorite = params.favorite ?? !this.isFavorite(target);
    // Recorded before the await so `track()` reports this attempt, not a stale
    // earlier one, whether the write succeeds or fails.
    this._lastToggle = { target, favorite };

    const queryKey = queryKeys.resource.favorites({ orgId: this.orgId });
    // Unfavoriting is applied to the cache at once — the row just drops, so
    // every star and the sidebar update instantly. Favoriting can't be: a new
    // row's name and icon come from the server, so it waits for the refetch.
    const rollback = favorite ? undefined : this.optimisticallyRemove(target);

    const result = await this.client.resource.favorites.set({
      type: target.type,
      id: target.id,
      favorite,
    });

    if (result.isErr()) {
      rollback?.();
      const message =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to update favorite';
      return err([{ t: 'error', message }]);
    }

    await this.queryClient.invalidateQueries({ queryKey });
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastToggle === null) {
      return null;
    }
    const { target, favorite } = this._lastToggle;
    return {
      event: favorite
        ? CocoEvent.ResourceFavorited
        : CocoEvent.ResourceUnfavorited,
      properties: {
        orgId: this.orgId,
        resourceType: target.type,
        resourceId: target.id,
      },
    };
  }

  /** Drops the row from the favorites cache; returns how to put it back. */
  private optimisticallyRemove(target: FavoriteTarget): () => void {
    const queryKey = queryKeys.resource.favorites({ orgId: this.orgId });
    const previous =
      this.queryClient.getQueryData<FolderItemsResponse>(queryKey);
    this.queryClient.setQueryData<FolderItemsResponse>(queryKey, (current) =>
      current === undefined
        ? current
        : {
            ...current,
            items: current.items.filter((item) => {
              const ref = favoriteRefOfItem(item);
              return !(ref.type === target.type && ref.id === target.id);
            }),
          }
    );
    return () => {
      if (previous !== undefined) {
        this.queryClient.setQueryData<FolderItemsResponse>(queryKey, previous);
      }
    };
  }

  /** Server-truth read from the favorites query cache. */
  private isFavorite(target: FavoriteTarget): boolean {
    const data = this.queryClient.getQueryData<{ items: FavoriteItem[] }>(
      queryKeys.resource.favorites({ orgId: this.orgId })
    );
    return (data?.items ?? []).some((item) => {
      const ref = favoriteRefOfItem(item);
      return ref.type === target.type && ref.id === target.id;
    });
  }

  private getTarget(
    params: Params,
    context: ApplicableContext
  ): FavoriteTarget | null {
    if (params.type !== undefined && params.id !== undefined) {
      return { type: params.type, id: params.id };
    }
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    const first = selection?.selections[0];
    if (first === undefined) {
      return null;
    }
    return { type: first.resourceType, id: first.resourceId };
  }
}
